const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_EDGE = 4096;

export interface PreparedImage {
  bitmap: ImageBitmap;
  sourceBlob: Blob;
  previewBlob: Blob;
  width: number;
  height: number;
}

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("无法生成图片")), type, quality));
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  if (file.size > MAX_FILE_BYTES) throw new Error("图片超过 20MB，请先压缩后再导入");
  if (!file.type.startsWith("image/")) throw new Error("请选择 PNG、JPEG、WebP 或系统可读取的 HEIC 图片");
  let bitmap: ImageBitmap;
  try { bitmap = await createImageBitmap(file, { imageOrientation: "from-image" }); }
  catch { throw new Error("当前 Safari 无法解码这张图片，请先保存为 JPG 或 PNG"); }
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) throw new Error("无法初始化图片处理画布");
  context.fillStyle = "#fff";
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const sourceBlob = await canvasBlob(canvas, "image/webp", 0.9);
  const previewCanvas = document.createElement("canvas");
  const previewScale = Math.min(1, 480 / Math.max(width, height));
  previewCanvas.width = Math.round(width * previewScale);
  previewCanvas.height = Math.round(height * previewScale);
  previewCanvas.getContext("2d")?.drawImage(canvas, 0, 0, previewCanvas.width, previewCanvas.height);
  const previewBlob = await canvasBlob(previewCanvas, "image/png");
  return { bitmap: await createImageBitmap(canvas), sourceBlob, previewBlob, width, height };
}
